import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Paper, Typography } from '@mui/material';
import { RefreshCw } from 'lucide-react';
import { useLayout } from '../../contexts/LayoutContext';
import PageContainer from '../../common_components/PageContainer';
import MetricCard from '../../common_components/MetricCard';
import { StyledCurrency } from '../../helper/StyledCurrency';
import CommonLoader from '../../common_components/CommonLoader';
import ClientGroupHeader from '../../common_components/ClientGroupHeader';
import AppButton from '../../common_components/AppButton';
import LastUpdatedInfo from '../../common_components/LastUpdatedInfo';
import AccessRestrictedContent from '../../common_components/AccessRestrictedContent';
import MonthlyBudgetSection from './components/MonthlyBudgetSection';
import BenchmarkHistoryModal from './components/BenchmarkHistoryModal';
import TestAdsSection from './components/TestAdsSection';
import UserGuideButton from './components/UserGuideButton';
import { useClientSelection, useAccountSelection } from '../../hooks';
import { setActiveModuleKey, clearPermissionDenied } from '../../redux/userDataSlice';
import { fetchFunnelSpend, clearFunnel, refreshBenchmark, refreshData, fetchRefreshStatus, selectFunnel, selectFunnelBoxes, selectFunnelLoading, selectFunnelError, selectRefreshingBenchmark, selectRefreshingData, selectTests, } from '../../redux/adTestingSlice';
import { toast } from '../../common_components/Toast/ToastUtils';
import adTestingService from '../../services/adTesting.service';
import PagesCommonHeader from '../../common_components/PagesCommonHeader';
const MODULE_KEY = 'ad_testing';
const TEST_TABS = [
    { key: 'microdata', label: 'MicroData Test' },
    { key: 'evergreen', label: 'Evergreen Ads' },
    { key: 'promo', label: 'Promo Test' },
];
// ── Formatters ───────────────────────────────────────────────────────────────
const fmtSpend = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Whole-number percentages across the funnel boxes + budget panels (per QA).
const fmtPct = (n) => `${Math.round(Math.abs(Number(n || 0)))}%`;
// Per-funnel icon shown to the left of each card title.
// TOF/MOF/BOF highlight the matching funnel level; Testing uses a stopwatch.
const FUNNEL_ICON = {
    tof: '/assets/tof.svg',
    mof: '/assets/mof.svg',
    bof: '/assets/bof.svg',
    test: '/assets/money-bag.svg',
};
const CardIcon = ({ funnelKey }) => (_jsx("img", { src: FUNNEL_ICON[funnelKey], alt: "", width: 18, height: 18, onError: (e) => { e.target.style.display = 'none'; } }));
// Top-right cluster: MoM comparison (% change + trend icon) followed by a bar-chart icon.
// The bar-chart icon opens the Benchmark History modal when onChartClick is provided.
const MomBadge = ({ pct, trend, onChartClick }) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.625 }, children: [_jsxs(Box, { sx: {
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                pl: '8px',
                pr: '6px',
                py: '2px',
                borderRadius: '16px',
                fontSize: '13px',
                fontWeight: 500,
                bgcolor: trend === 'down' ? '#FEF3F2' : '#ECFDF3',
                color: trend === 'down' ? '#B42318' : '#067647',
            }, children: [_jsx(StyledCurrency, { value: fmtPct(pct), symbolColor: trend === 'down' ? '#B42318' : '#067647', valueColor: trend === 'down' ? '#B42318' : '#067647', fontSize: "13px", symbolSize: "13px", valueWeight: 500, symbolWeight: 500 }), _jsx("img", { src: trend === 'down' ? '/assets/trending-down.svg' : '/assets/trend-up.svg', alt: trend, width: 12, height: 12, onError: (e) => { e.target.style.display = 'none'; } })] }), onChartClick && (_jsx(Box, { onClick: onChartClick, sx: {
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: '4px',
                borderRadius: '16px',
                bgcolor: '#EFF8FF',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#E0F0FF' },
            }, children: _jsx("img", { src: "/assets/bar-chart-10.svg", alt: "", width: 12, height: 12, onError: (e) => { e.target.style.display = 'none'; } }) }))] }));
// Full-width segmented control for the test-type tabs (figma: solid magenta active).
const TestTypeTabs = ({ value, onChange }) => (_jsx(Box, { sx: {
        display: 'flex',
        gap: '4px',
        p: '4px',
        bgcolor: '#F9FAFB',
        border: '1px solid #EAECF0',
        borderRadius: '8px',
    }, children: TEST_TABS.map((tab) => {
        const isActive = value === tab.key;
        return (_jsx(Box, { onClick: () => onChange(tab.key), className: "all-Font-Sizes", sx: {
                flex: 1,
                textAlign: 'center',
                py: '8px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: isActive ? 600 : 500,
                color: isActive ? '#FFFFFF' : '#667085',
                bgcolor: isActive ? '#FF008E' : 'transparent',
                userSelect: 'none',
                '&:hover': { bgcolor: isActive ? '#FF008E' : '#F2F4F7' },
            }, children: tab.label }, tab.key));
    }) }));
const AdTesting = () => {
    const dispatch = useDispatch();
    // Initial tab honours a ?category= deep link (e.g. from the result email); else MicroData.
    const [activeTab, setActiveTab] = useState(() => {
        const c = new URLSearchParams(window.location.search).get('category');
        return c === 'evergreen' || c === 'promo' || c === 'microdata' ? c : 'microdata';
    });
    const [benchmark, setBenchmark] = useState({ open: false, funnel: 'tof' });
    // Collapse the left nav by default on this wide page; restore the prior state on leave.
    const { isSidebarExpanded, setIsSidebarExpanded } = useLayout();
    const sidebarWasExpanded = useRef(isSidebarExpanded);
    useEffect(() => {
        setIsSidebarExpanded(false);
        return () => setIsSidebarExpanded(sidebarWasExpanded.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Module access + action gating
    const permissions = useSelector((state) => state.auth.permissions);
    const modulePerms = permissions?.[MODULE_KEY];
    const hasAccess = modulePerms?.access !== false;
    const canRefresh = !!modulePerms?.refresh;
    const canEditBudget = Array.isArray(modulePerms?.actions) && modulePerms.actions.includes('Testing Budget');
    // Client (+ group) selection — meta-connected clients only (driven by x-module-key)
    const { selectedId, selectedClientId, selectedGroupAccountId, clientOptions, groupAccountOptions, currentSelectedGroupAccountLabel, handleClientChange, handleGroupChange, } = useClientSelection(MODULE_KEY, { filterMeta: true });
    // Account (Meta ad account) options for the chosen client (selection managed
    // locally below so we can prepend an "All Accounts" option).
    const { accountOptions, accountLoading } = useAccountSelection({ selectedId });
    // "All Accounts" is offered (and default) whenever the client has >1 Meta account.
    const accountDropdownOptions = useMemo(() => accountOptions.length > 1
        ? [{ value: 'all', label: 'All Accounts' }, ...accountOptions]
        : accountOptions, [accountOptions]);
    const showAccountField = accountOptions.length > 1;
    const [selectedAccount, setSelectedAccount] = useState('');
    // Reset the account the instant the client changes — during render, before effects
    // run — so the funnel/tests fetches never fire the NEW client with the PREVIOUS
    // client's connectionId (that mismatch 404s until the account reloads). The account
    // is re-set below once the new client's options load. (React "adjust state during
    // render" pattern.)
    const [accountClient, setAccountClient] = useState(selectedId);
    if (selectedId !== accountClient) {
        setAccountClient(selectedId);
        if (selectedAccount !== '')
            setSelectedAccount('');
    }
    useEffect(() => {
        if (!accountOptions.length) {
            setSelectedAccount('');
            return;
        }
        // Default to "All" when multiple accounts, else the single account.
        setSelectedAccount(accountOptions.length > 1 ? 'all' : accountOptions[0].value);
    }, [accountOptions]);
    const currentAccountLabel = useMemo(() => accountDropdownOptions.find((o) => o.value === selectedAccount)?.label || 'Select Account', [accountDropdownOptions, selectedAccount]);
    // ── Persist the client selection per user in Redis so a refresh keeps the same
    // client (independent of localStorage, which is per-browser and skipped for
    // assigned-scope users). Load the saved selection on mount, apply it once the
    // client list is ready, then save every change back.
    const token = useSelector((s) => s.auth?.token || '');
    const savedSelRef = useRef(null);
    const [selectionLoaded, setSelectionLoaded] = useState(false);
    const restoredSelRef = useRef(false);
    const [canPersistSel, setCanPersistSel] = useState(false);
    // 1. Load the saved selection once a token is available.
    useEffect(() => {
        if (!token)
            return;
        let alive = true;
        (async () => {
            try {
                const res = await adTestingService.getSelection(token);
                if (alive)
                    savedSelRef.current = res?.data?.data || null;
            }
            catch {
                /* ignore — fall back to the default selection */
            }
            finally {
                if (alive)
                    setSelectionLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [token]);
    // 2. Apply the saved selection once (after the client list is available). The
    // effective saved id (group store, else client) restores parent + group in one
    // call. canPersist flips on only AFTER this, so the hook's transient first-client
    // default never overwrites the user's saved choice.
    useEffect(() => {
        if (restoredSelRef.current || !selectionLoaded || !clientOptions.length)
            return;
        restoredSelRef.current = true;
        const saved = savedSelRef.current;
        const effId = saved?.groupAccountId || saved?.clientId || '';
        if (effId && effId !== selectedId && clientOptions.some((o) => o.value === effId)) {
            handleClientChange(effId);
        }
        setCanPersistSel(true);
    }, [selectionLoaded, clientOptions, selectedId, handleClientChange]);
    // 3. Save every selection change back to Redis (after the restore attempt).
    useEffect(() => {
        if (!canPersistSel || !selectedClientId)
            return;
        adTestingService
            .saveSelection({ clientId: selectedClientId, groupAccountId: selectedGroupAccountId || '' }, token)
            .catch(() => { });
    }, [canPersistSel, selectedClientId, selectedGroupAccountId, token]);
    const boxes = useSelector(selectFunnelBoxes);
    const funnel = useSelector(selectFunnel);
    // Previous-month total spend → drives each card's MoM contribution % pill.
    const prevTotalSpend = funnel?.previous_total_spend || 0;
    const loading = useSelector(selectFunnelLoading);
    const error = useSelector(selectFunnelError);
    const refreshingBenchmark = useSelector(selectRefreshingBenchmark);
    const refreshingData = useSelector(selectRefreshingData);
    // "Last Updated at" for the visible tab (latest meta_testing_ads.updatedAt), from getTests.
    const tests = useSelector(selectTests);
    // On-demand refresh: after kicking off the job, poll until it finishes, then reload
    // the data (non-blocking — the page stays usable while it runs).
    const [refreshInProgress, setRefreshInProgress] = useState(false);
    // Bumped when a refresh completes → child sections (tests / budget) re-fetch.
    const [refreshNonce, setRefreshNonce] = useState(0);
    const activeRefreshRef = useRef(false);
    const pollRef = useRef(null);
    // Register module key so the axios helper injects x-module-key on every call.
    // Also clear any stale global "permission denied" (a prior 403 sets it and it can
    // persist) so a deep link into this page — e.g. the Ad Testing result email — isn't
    // blocked by a leftover Access Restricted overlay. Real permission is still enforced
    // by this page's own hasAccess gate and the server-side module/client checks.
    useEffect(() => {
        dispatch(setActiveModuleKey(MODULE_KEY));
        dispatch(clearPermissionDenied());
    }, [dispatch]);
    const loadFunnelSpend = useCallback(async () => {
        if (!selectedId || !selectedAccount)
            return;
        await dispatch(fetchFunnelSpend({ clientId: selectedId, connectionId: selectedAccount }));
    }, [dispatch, selectedId, selectedAccount]);
    // "Refresh Benchmark" — runs the automated benchmark generator for this client.
    const handleRefreshBenchmark = useCallback(async () => {
        if (!selectedId)
            return;
        try {
            await dispatch(refreshBenchmark({ clientId: selectedId })).unwrap();
            toast.success('Benchmark generated');
        }
        catch (e) {
            toast.error(typeof e === 'string' ? e : 'Failed to refresh benchmark');
        }
    }, [dispatch, selectedId]);
    // Refresh-data icon — kicks off a full Meta sync (last 31 days) + meta_testing_ads
    // rebuild in the background, then polls the job until it finishes and reloads the
    // data. Non-blocking: the page stays usable, the icon spins + a "Refreshing…" note
    // shows. Safety cap so it never spins forever.
    const handleRefreshData = useCallback(async () => {
        if (!selectedId || refreshInProgress)
            return;
        try {
            await dispatch(refreshData({ clientId: selectedId })).unwrap();
        }
        catch (e) {
            toast.error(typeof e === 'string' ? e : 'Failed to start refresh');
            return;
        }
        const clientForRun = selectedId;
        const startedAt = Date.now();
        const MAX_WAIT_MS = 8 * 60 * 1000;
        activeRefreshRef.current = true;
        setRefreshInProgress(true);
        toast.info('Refreshing — this can take a few minutes.');
        const finish = (msg, kind) => {
            activeRefreshRef.current = false;
            if (pollRef.current) {
                clearTimeout(pollRef.current);
                pollRef.current = null;
            }
            setRefreshInProgress(false);
            if (kind === 'success')
                toast.success(msg);
            else
                toast.info(msg);
        };
        const poll = async () => {
            if (!activeRefreshRef.current)
                return;
            let inFlight = false;
            try {
                inFlight = await dispatch(fetchRefreshStatus({ clientId: clientForRun })).unwrap();
            }
            catch {
                inFlight = false;
            }
            if (!activeRefreshRef.current)
                return; // cancelled (client switch / unmount) mid-poll
            if (!inFlight) {
                loadFunnelSpend(); // reload funnel boxes
                setRefreshNonce((n) => n + 1); // → tests + monthly-budget re-fetch
                finish('Data refreshed.', 'success');
                return;
            }
            if (Date.now() - startedAt > MAX_WAIT_MS) {
                finish('Still processing — the data will update shortly.', 'info');
                return;
            }
            pollRef.current = setTimeout(poll, 5000);
        };
        pollRef.current = setTimeout(poll, 5000);
    }, [dispatch, selectedId, refreshInProgress, loadFunnelSpend]);
    // Abandon any in-flight refresh poll when the client changes.
    useEffect(() => {
        activeRefreshRef.current = false;
        if (pollRef.current) {
            clearTimeout(pollRef.current);
            pollRef.current = null;
        }
        setRefreshInProgress(false);
    }, [selectedId]);
    // Stop polling on unmount.
    useEffect(() => () => {
        activeRefreshRef.current = false;
        if (pollRef.current)
            clearTimeout(pollRef.current);
    }, []);
    // Fetch funnel spend whenever the selected client/account changes
    useEffect(() => {
        if (!selectedId || !selectedAccount) {
            dispatch(clearFunnel());
            return;
        }
        loadFunnelSpend();
    }, [dispatch, selectedId, selectedAccount, loadFunnelSpend]);
    if (!hasAccess) {
        return (_jsx(PageContainer, { children: _jsx(AccessRestrictedContent, {}) }));
    }
    const showBoxes = !!selectedId && !!selectedAccount;
    // Whole-page overlay loader — ONLY for the heavy loads that refresh the funnel /
    // account (initial load, client or account change, refresh). Tab changes (test-type
    // or Running / Past History) reload just the table (and budget), which show their own
    // section-level loaders instead of covering the whole page.
    const pageLoading = loading || accountLoading;
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsx(ClientGroupHeader, { selectedClientId: selectedClientId, clientOptions: clientOptions, handleClientChange: handleClientChange, selectedGroupAccountId: selectedGroupAccountId, groupAccountOptions: groupAccountOptions, handleGroupChange: handleGroupChange, currentSelectedGroupAccountLabel: currentSelectedGroupAccountLabel, showAccountsField: showAccountField, selectedAccountId: selectedAccount, accountOptions: accountDropdownOptions, currentSelectedAccountLabel: currentAccountLabel, onAccountChange: setSelectedAccount, isRefreshing: loading || accountLoading, endSlot: _jsx(UserGuideButton, {}) }), _jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsx(_Fragment, { children: showBoxes && (_jsx(Box, { sx: {
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                    // mt: 2,
                                }, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }, children: [_jsx(LastUpdatedInfo, { updatedAt: tests?.last_updated_at ?? null, moduleName: MODULE_KEY, 
                                            // Wire the refresh-data icon → on-demand Meta sync + test rebuild.
                                            onRefresh: handleRefreshData, 
                                            // Spin + block re-clicks for the whole run (POST + poll).
                                            loading: refreshInProgress || refreshingData, disabled: !selectedId || refreshInProgress }), refreshInProgress && (_jsx(Typography, { sx: { fontSize: '12px', color: '#667085', fontWeight: 500 }, children: "Refreshing\u2026 this can take a few minutes" }))] }) })) }), rightSection: _jsx(_Fragment, { children: canRefresh && (_jsx(AppButton, { variant: "outlined", onClick: handleRefreshBenchmark, disabled: refreshingBenchmark, startIcon: _jsx(Box, { component: "span", sx: {
                                        display: 'inline-flex',
                                        ...(refreshingBenchmark && {
                                            animation: 'adtSpin 1s linear infinite',
                                            '@keyframes adtSpin': {
                                                from: { transform: 'rotate(0deg)' },
                                                to: { transform: 'rotate(360deg)' },
                                            },
                                        }),
                                    }, children: _jsx(RefreshCw, { size: 16 }) }), sx: {
                                    textTransform: 'none',
                                    color: '#344054',
                                    borderColor: '#D0D5DD',
                                    bgcolor: '#FFFFFF',
                                    '&:hover': { borderColor: '#98A2B3', bgcolor: '#F9FAFB' },
                                }, children: "Refresh Benchmark" })) }) }), !showBoxes ? (_jsx(Box, { sx: { mt: 2 }, children: _jsx(Paper, { elevation: 0, sx: { border: '1px solid #EAECF0', borderRadius: '8px', p: 4, textAlign: 'center' }, children: _jsx(Typography, { sx: { color: '#667085', fontSize: '14px' }, children: "Select a client and Meta account to view funnel spend." }) }) })) : (
                    // One relative container for the whole data area; a single overlay
                    // loader (below) covers all sections while anything is loading.
                    _jsxs(Box, { sx: { position: 'relative', mt: 2, minHeight: pageLoading ? 360 : undefined }, children: [error ? (_jsx(Paper, { elevation: 0, sx: { border: '1px solid #FECDCA', borderRadius: '8px', p: 4, textAlign: 'center', bgcolor: '#FFFBFA' }, children: _jsx(Typography, { sx: { color: '#B42318', fontSize: '14px' }, children: error }) })) : (_jsx(Box, { sx: {
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                                    gap: '12px',
                                }, children: boxes.map((box) => (_jsx(MetricCard, { title: box.label, icon: _jsx(CardIcon, { funnelKey: box.key }), prefix: "$", value: fmtSpend(box.spend), contributionPercentage: fmtPct(box.pct_of_total), contributionPill: true, comparisonPrefix: "$", comparisonLabel: "MoM", comparisonValue: fmtSpend(box.previous_spend), comparisonBadge: fmtPct(prevTotalSpend > 0 ? (box.previous_spend / prevTotalSpend) * 100 : 0), rightAction: _jsx(MomBadge, { pct: box.mom_pct_change, trend: box.trend, onChartClick: box.key !== 'test'
                                            ? () => setBenchmark({ open: true, funnel: box.key })
                                            : undefined }) }, box.key))) })), _jsx(Box, { sx: { mt: 2 }, children: _jsx(TestTypeTabs, { value: activeTab, onChange: setActiveTab }) }), activeTab === 'microdata' && (_jsx(MonthlyBudgetSection, { clientId: selectedId, canEditBudget: canEditBudget, suppressLoader: pageLoading, refreshNonce: refreshNonce })), _jsx(TestAdsSection, { clientId: selectedId, connectionId: selectedAccount, category: activeTab, suppressLoader: pageLoading, refreshNonce: refreshNonce }, activeTab), pageLoading && (_jsx(Box, { sx: {
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(255, 255, 255, 0.72)',
                                    borderRadius: '8px',
                                }, children: _jsx(CommonLoader, {}) }))] }))] }), _jsx(BenchmarkHistoryModal, { open: benchmark.open, onClose: () => setBenchmark((b) => ({ ...b, open: false })), clientId: selectedId, connectionId: selectedAccount, funnel: benchmark.funnel, title: `${benchmark.funnel.toUpperCase()} Benchmark History` })] }));
};
export default AdTesting;
